/**
 * §B-4 readiness-rule editor — a Drawer form (name/priority/effect/scope/status)
 * + the shared condition builder driven by the READINESS field catalog. Saves via
 * the ADMIN write API with optimistic concurrency (409 → reload).
 */
import { useState } from "react";
import { ApiError, api, type AdminReadinessRule, type ReadinessRuleInput } from "../../../lib/api.js";
import { READINESS_FIELDS, conditionToBuilder, builderToCondition, validateBuilder, type BuilderState } from "../../../lib/conditionModel.js";
import type { ReadinessEffect } from "@cairn/shared";
import { Drawer, Field, Input, Select, Button, useToast } from "../../../components/ui/index.js";
import { ConditionBuilder } from "../ConditionBuilder.js";

interface StoreOpt {
  id: string;
  label: string;
}

export function ReadinessRuleEditor({
  rule,
  stores,
  onClose,
  onSaved,
}: {
  rule: AdminReadinessRule | null;
  stores: StoreOpt[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const isNew = rule === null;

  const [name, setName] = useState(rule?.name ?? "");
  const [priority, setPriority] = useState(String(rule?.priority ?? 100));
  const [effect, setEffect] = useState<ReadinessEffect>(rule?.effect ?? "REQUIRE");
  const [scope, setScope] = useState<string>(rule?.storeId ?? "");
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [builder, setBuilder] = useState<BuilderState>(() => conditionToBuilder(rule?.condition ?? { all: [] }));
  const [saving, setSaving] = useState(false);

  async function save() {
    const err = validateBuilder(builder, READINESS_FIELDS);
    if (err) {
      toast(err, "danger");
      return;
    }
    if (!name.trim()) {
      toast("Give the rule a name.", "danger");
      return;
    }
    const prio = Number(priority);
    if (!Number.isInteger(prio)) {
      toast("Priority must be a whole number.", "danger");
      return;
    }
    let condition;
    try {
      condition = builderToCondition(builder, READINESS_FIELDS);
    } catch {
      toast("Condition JSON is not valid.", "danger");
      return;
    }
    const input: ReadinessRuleInput = { name: name.trim(), priority: prio, condition, effect, enabled, storeId: scope || null };
    setSaving(true);
    try {
      if (isNew) await api.admin.createReadinessRule(input);
      else await api.admin.updateReadinessRule(rule.id, input, rule.updatedAt);
      toast(isNew ? "Rule created" : "Rule saved", "success");
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast("This rule changed since you opened it — reloading.", "danger");
        onSaved();
      } else if (e instanceof ApiError && e.status === 400) {
        toast((e.detail as { error?: string } | undefined)?.error ?? "Invalid rule.", "danger");
      } else if (e instanceof ApiError && e.status === 403) {
        toast("Admin role required to edit rules.", "danger");
      } else {
        toast("Couldn’t save the rule.", "danger");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      onClose={onClose}
      ariaLabel={isNew ? "New readiness rule" : `Edit ${rule.name}`}
      title={isNew ? "New readiness rule" : "Edit readiness rule"}
      meta={<span className="muted">REQUIRE = must be true to ship · BLOCK = holds when true.</span>}
    >
      <div className="rule-form">
        <div className="rule-form__row2">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Address must be valid" autoFocus />
          </Field>
          <Field label="Priority (lower runs first)">
            <Input value={priority} onChange={(e) => setPriority(e.target.value)} inputMode="numeric" />
          </Field>
        </div>

        <div className="rule-form__row2">
          <Field label="Effect">
            <Select
              ariaLabel="Effect"
              value={effect}
              onChange={(v) => setEffect(v as ReadinessEffect)}
              options={[
                { value: "REQUIRE", label: "REQUIRE (must be true to ship)" },
                { value: "BLOCK", label: "BLOCK (holds when true)" },
              ]}
            />
          </Field>
          <Field label="Status">
            <label className="rule-form__toggle">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span>{enabled ? "Enabled" : "Disabled"}</span>
            </label>
          </Field>
        </div>

        <div className="rule-form__row2">
          <Field label="Scope">
            <Select
              ariaLabel="Scope"
              value={scope}
              onChange={setScope}
              options={[{ value: "", label: "All stores" }, ...stores.map((s) => ({ value: s.id, label: s.label }))]}
            />
          </Field>
          <span />
        </div>

        <ConditionBuilder fields={READINESS_FIELDS} builder={builder} onChange={setBuilder} />

        <div className="rule-form__actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" icon="check" onClick={() => void save()} disabled={saving}>
            {saving ? "Saving…" : isNew ? "Create rule" : "Save changes"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
